<template>
  <div v-if="visible" class="import-overlay" @click.self="cancel">
    <div class="import-dialog">
      <!-- 标题栏 -->
      <div class="dialog-header">
        <span class="dialog-title">导入 3D 模型</span>
        <button class="close-btn" @click="cancel"><X :size="18" /></button>
      </div>

      <!-- 步骤1：文件选择 -->
      <div v-if="step === 'select'" class="step-select">
        <div
          class="drop-zone"
          :class="{ 'dragging': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          @drop.prevent="onFileDrop"
          @click="openFileChooser"
        >
          <FolderOpen :size="40" class="drop-icon" />
          <p class="drop-main">拖拽文件到此处</p>
          <p class="drop-sub">或点击选择文件</p>
          <p class="drop-formats">支持格式：GLB · GLTF · OBJ · STL · FBX · DAE · 3DS</p>
          <p class="drop-formats-sub">分离式 glTF 请与 .bin / 贴图一并选中（主文件为 .gltf，不能单选 .bin）</p>
        </div>
        <input
          ref="fileInputRef"
          type="file"
          style="display:none"
          :accept="acceptAttr"
          multiple
          @change="onFileInputChange"
        />
        <div class="tip-box">
          <p class="tip-text">SolidWorks / CATIA 用户：请先导出为 STEP，再用 FreeCAD 转换为 GLB 格式后导入</p>
        </div>
      </div>

      <!-- 步骤2：加载中 -->
      <div v-else-if="step === 'loading'" class="step-loading">
        <Loader2 :size="36" class="spin-icon" />
        <p class="loading-text">{{ loadingText }}</p>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progress + '%' }"></div>
        </div>
        <p class="progress-label">{{ progress }}%</p>
      </div>

      <!-- 步骤3：预览 + 设置 -->
      <div v-else-if="step === 'preview'" class="step-preview">
        <div class="preview-layout">
          <!-- 左：3D 预览 -->
          <div ref="previewContainerRef" class="preview-3d"></div>

          <!-- 右：设置项 -->
          <div class="preview-settings">
            <div class="setting-row">
              <label class="setting-label">模型名称</label>
              <input v-model="modelName" class="setting-input" maxlength="40" />
            </div>

            <div class="setting-row">
              <label class="setting-label">检测尺寸</label>
              <span class="setting-value">{{ dimensionText }}</span>
            </div>

            <div v-if="showUnitWarning" class="unit-warning">
              <AlertTriangle :size="14" class="warning-icon" />
              <span>模型非常小（可能是毫米单位）</span>
            </div>

            <div class="setting-row">
              <label class="setting-label">原始单位</label>
              <select v-model="selectedUnit" class="setting-select">
                <option value="auto">自动检测</option>
                <option value="m">米（m）</option>
                <option value="mm">毫米（mm）</option>
                <option value="cm">厘米（cm）</option>
                <option value="inch">英寸（inch）</option>
              </select>
            </div>

            <div class="setting-row face-count-row">
              <label class="setting-label">面数</label>
              <span class="setting-value" :class="faceCountClass">{{ faceCountText }}</span>
            </div>

            <!-- 面数警告 -->
            <div v-if="faceCount > 500000" class="face-warning">
              <AlertTriangle :size="14" class="warning-icon" />
              <span>模型面数过多（{{ Math.round(faceCount / 10000) }}万），可能影响性能</span>
            </div>
            <div v-else-if="faceCount > 100000" class="face-notice">
              <Info :size="14" />
              <span>模型较复杂（{{ Math.round(faceCount / 10000) }}万面），编辑时可能稍慢</span>
            </div>
          </div>
        </div>

        <div class="dialog-footer">
          <button class="btn-cancel" @click="cancel">取消</button>
          <button class="btn-confirm" @click="confirmImport" :disabled="!modelName.trim()">
            <Check :size="16" />
            放入场景
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { X, FolderOpen, Loader2, AlertTriangle, Info, Check } from 'lucide-vue-next'
import { v4 as uuidv4 } from 'uuid'
import { loadModel, getFormatFromFilename, exportGroupToGLB, type SupportedFormat } from '../three/ModelImporter'
import { renderThumbnail } from '../three/ThumbnailRenderer'
import { getStorage } from '../storage'
import { useModelLibraryStore } from '../stores/modelLibrary'
import { useSceneStore } from '../stores/scene'

const emit = defineEmits<{
  close: []
  imported: [libraryId: string]
}>()

const props = defineProps<{ visible: boolean; initialFiles?: File[] }>()

const modelLibraryStore = useModelLibraryStore()
const sceneStore = useSceneStore()

const step = ref<'select' | 'loading' | 'preview'>('select')
const isDragging = ref(false)
const loadingText = ref('正在解析文件...')
const progress = ref(0)
const modelName = ref('')
const selectedUnit = ref<'auto' | 'm' | 'mm' | 'cm' | 'inch'>('auto')
const faceCount = ref(0)
const detectedMaxDim = ref(0)
const detectedUnitScale = ref(1)
const currentFormat = ref<SupportedFormat>('glb')
const currentLibraryId = ref('')
const currentBuffer = ref<ArrayBuffer | null>(null)
const currentExtraFiles = ref<{ name: string; buffer: ArrayBuffer }[]>([])
const fileInputRef = ref<HTMLInputElement | null>(null)
const previewContainerRef = ref<HTMLDivElement | null>(null)

let previewRenderer: THREE.WebGLRenderer | null = null
let previewScene: THREE.Scene | null = null
let previewCamera: THREE.PerspectiveCamera | null = null
let previewControls: OrbitControls | null = null
let previewAnimId = 0
let loadedGroup: THREE.Group | null = null

const acceptAttr = '.glb,.gltf,.obj,.stl,.fbx,.dae,.3ds'

const showUnitWarning = computed(() => detectedMaxDim.value < 0.1 && detectedMaxDim.value > 0)

const dimensionText = computed(() => {
  if (!detectedMaxDim.value) return '—'
  return `最大轴 ${detectedMaxDim.value.toFixed(4)} m`
})

const effectiveUnitScale = computed(() => {
  switch (selectedUnit.value) {
    case 'auto': return detectedUnitScale.value
    case 'm': return 1
    case 'mm': return 0.001
    case 'cm': return 0.01
    case 'inch': return 0.0254
    default: return 1
  }
})

const faceCountText = computed(() => {
  if (!faceCount.value) return '—'
  if (faceCount.value > 10000) return `${Math.round(faceCount.value / 10000)} 万`
  return `${faceCount.value}`
})

const faceCountClass = computed(() => {
  if (faceCount.value > 500000) return 'face-danger'
  if (faceCount.value > 100000) return 'face-warn'
  return ''
})

function openFileChooser() {
  fileInputRef.value?.click()
}

function onFileInputChange(e: Event) {
  const files = (e.target as HTMLInputElement).files
  if (files && files.length > 0) processFiles(Array.from(files))
}

function onFileDrop(e: DragEvent) {
  isDragging.value = false
  const files = e.dataTransfer?.files
  if (files && files.length > 0) processFiles(Array.from(files))
}

/** 主模型文件：优先 .gltf/.glb 等，避免把同批的 .bin 误当成主文件 */
function pickMainImportFile(files: File[]): File | null {
  const dxf = files.find(f => f.name.toLowerCase().endsWith('.dxf'))
  if (dxf) return dxf

  const priority = ['.gltf', '.glb', '.obj', '.stl', '.fbx', '.dae', '.3ds']
  const candidates = files.filter(f => getFormatFromFilename(f.name) !== null)
  if (candidates.length === 0) return null

  candidates.sort((a, b) => {
    const extA = '.' + (a.name.split('.').pop()?.toLowerCase() ?? '')
    const extB = '.' + (b.name.split('.').pop()?.toLowerCase() ?? '')
    const iA = priority.indexOf(extA)
    const iB = priority.indexOf(extB)
    const oA = iA === -1 ? 999 : iA
    const oB = iB === -1 ? 999 : iB
    if (oA !== oB) return oA - oB
    return a.name.localeCompare(b.name)
  })
  return candidates[0] ?? null
}

async function processFiles(files: File[]) {
  const mainFile = pickMainImportFile(files)
  if (!mainFile) {
    ElMessage.warning(
      '未找到可导入的主模型文件。请选择 .gltf / .glb / .obj 等；分离式 glTF 的 .bin 与贴图需与 .gltf 同一批选中，不能单独作为主文件。'
    )
    return
  }

  // DXF 单独处理：上传云端（内容寻址）并放入场景
  if (mainFile.name.toLowerCase().endsWith('.dxf')) {
    const buffer = await mainFile.arrayBuffer()
    const ref = await getStorage().assets.put(new File([buffer], mainFile.name, { type: 'application/dxf' }))
    const name = mainFile.name.replace(/\.[^.]+$/, '')
    await modelLibraryStore.addModel({
      libraryId: ref.sha256, name, format: 'dxf',
      originalFilename: mainFile.name,
      thumbnail: '',
      createdAt: new Date().toISOString(),
      unitScale: 0.001,
      sizeBytes: ref.sizeBytes,
    })
    // 放入场景作地面参考
    const obj = {
      id: uuidv4(), type: 'dxf_reference', name,
      position: [0, 0, 0] as [number, number, number],
      rotationY: 0,     params: { userScale: 1 },
    label: { text: name, visible: false },
    importedModel: {
      format: 'dxf' as const, source: 'library' as const,
      libraryId: ref.sha256, originalFilename: mainFile.name, unitScale: 0.001
    }
    }
    sceneStore.addObject(obj as any)
    emit('imported', ref.sha256)
    emit('close')
    return
  }

  const format = getFormatFromFilename(mainFile.name)
  if (!format) {
    ElMessage.warning(`不支持的文件格式：${mainFile.name}`)
    return
  }

  step.value = 'loading'
  progress.value = 10
  loadingText.value = '正在读取文件...'

  const buffer = await mainFile.arrayBuffer()
  progress.value = 30

  // 读取附加文件（如 .mtl）
  const extras: { name: string; buffer: ArrayBuffer }[] = []
  for (const f of files) {
    if (f !== mainFile) {
      extras.push({ name: f.name, buffer: await f.arrayBuffer() })
    }
  }

  loadingText.value = '正在解析几何体...'
  progress.value = 50

  try {
    const result = await loadModel(buffer, format, extras)
    progress.value = 80
    loadingText.value = '生成预览...'

    currentFormat.value = format
    currentBuffer.value = buffer
    currentExtraFiles.value = extras
    currentLibraryId.value = uuidv4()
    loadedGroup = result.group
    faceCount.value = result.faceCount
    detectedMaxDim.value = result.maxDimension
    detectedUnitScale.value = result.unitScale

    // 自动检测单位
    if (result.unitScale === 0.001) {
      selectedUnit.value = 'auto'
    }

    modelName.value = mainFile.name.replace(/\.[^.]+$/, '')
    progress.value = 100

    step.value = 'preview'

    // 等 DOM 渲染后初始化预览
    setTimeout(() => initPreview(result.group), 100)
  } catch (err) {
    console.error('模型解析失败', err)
    const hint =
      format === 'gltf'
        ? '（分离式 glTF 需同时选中 .gltf 与配套的 .bin / 贴图，或改用单个 .glb）'
        : ''
    ElMessage.error(`模型解析失败：${String(err)}${hint ? `（${hint}）` : ''}`)
    step.value = 'select'
  }
}

function initPreview(group: THREE.Group) {
  const container = previewContainerRef.value
  if (!container) return
  cleanupPreview()

  const w = container.clientWidth || 240
  const h = container.clientHeight || 240

  previewRenderer = new THREE.WebGLRenderer({ antialias: true })
  previewRenderer.setSize(w, h)
  previewRenderer.setClearColor('#1a2332')
  container.appendChild(previewRenderer.domElement)

  previewScene = new THREE.Scene()
  previewScene.background = new THREE.Color('#1a2332')

  const ambient = new THREE.AmbientLight(0xffffff, 0.7)
  previewScene.add(ambient)
  const dir = new THREE.DirectionalLight(0xffffff, 0.8)
  dir.position.set(5, 8, 5)
  previewScene.add(dir)

  // 克隆 group 用于预览
  const clone = group.clone(true)

  // 应用单位缩放
  const scale = effectiveUnitScale.value
  clone.scale.setScalar(scale)

  previewScene.add(clone)

  // 自适应相机
  const box = new THREE.Box3().setFromObject(clone)
  const center = new THREE.Vector3()
  box.getCenter(center)
  const size = new THREE.Vector3()
  box.getSize(size)
  const maxDim = Math.max(size.x, size.y, size.z)

  previewCamera = new THREE.PerspectiveCamera(50, w / h, maxDim * 0.001, maxDim * 100)
  previewCamera.position.set(
    center.x + maxDim * 1.5,
    center.y + maxDim * 0.8,
    center.z + maxDim * 1.5
  )
  previewCamera.lookAt(center)

  previewControls = new OrbitControls(previewCamera, previewRenderer.domElement)
  previewControls.target.copy(center)
  previewControls.enableDamping = true
  previewControls.update()

  const loop = () => {
    previewAnimId = requestAnimationFrame(loop)
    previewControls?.update()
    previewRenderer?.render(previewScene!, previewCamera!)
  }
  loop()
}

function cleanupPreview() {
  cancelAnimationFrame(previewAnimId)
  previewControls?.dispose()
  previewRenderer?.dispose()
  if (previewContainerRef.value && previewRenderer) {
    previewContainerRef.value.removeChild(previewRenderer.domElement)
  }
  previewRenderer = null
  previewScene = null
  previewCamera = null
  previewControls = null
}

// 当单位变化时重新构建预览
watch(selectedUnit, () => {
  if (step.value === 'preview' && loadedGroup) {
    initPreview(loadedGroup)
  }
})

async function confirmImport() {
  if (!currentBuffer.value || !loadedGroup) return

  const name = modelName.value.trim() || '导入模型'

  // 分离式 glTF：IndexedDB 只存主文件会导致场景再次 load 时缺 .bin/贴图 → 打包为单 GLB 再入库
  let bufferToStore: ArrayBuffer = currentBuffer.value
  let formatStored: SupportedFormat = currentFormat.value
  if (currentFormat.value === 'gltf' && currentExtraFiles.value.length > 0) {
    try {
      bufferToStore = await exportGroupToGLB(loadedGroup)
      formatStored = 'glb'
    } catch (e) {
      console.error('打包 GLB 失败', e)
      ElMessage.error(`无法将模型打包为 GLB 以便保存：${String(e)}`)
      return
    }
  }

  // 上传云端（内容寻址，重复内容秒跳）→ 登记模型库 → 放入场景（libraryId = sha256）
  const ref = await getStorage().assets.put(
    new File([bufferToStore], `${name}.${formatStored}`, { type: formatStored === 'glb' ? 'model/gltf-binary' : 'application/octet-stream' }),
  )

  // 生成缩略图
  let thumbnail = ''
  try {
    const cloneForThumb = loadedGroup.clone(true)
    cloneForThumb.scale.setScalar(effectiveUnitScale.value)
    thumbnail = renderThumbnail(cloneForThumb)
  } catch (e) {
    console.warn('缩略图生成失败', e)
  }

  // 存入模型库（单位与放入场景一致，供「我的模型」再次放置时使用）
  await modelLibraryStore.addModel({
    libraryId: ref.sha256,
    name,
    format: formatStored,
    originalFilename: modelName.value,
    thumbnail,
    createdAt: new Date().toISOString(),
    unitScale: effectiveUnitScale.value,
    sizeBytes: ref.sizeBytes,
  })

  // 放入场景
  placeModelInScene(ref.sha256, name, formatStored)

  cleanupPreview()
  emit('imported', ref.sha256)
  emit('close')
  resetState()
}

function placeModelInScene(libraryId: string, name: string, format: SupportedFormat) {
  const obj = {
    id: uuidv4(),
    type: 'imported_model',
    name,
    position: [0, 0, 0] as [number, number, number],
    rotationY: 0,
    params: { userScale: 1 },
    label: { text: name, visible: true },
    importedModel: {
      format,
      source: 'library' as const,
      libraryId,
      originalFilename: modelName.value,
      unitScale: effectiveUnitScale.value
    }
  }
  sceneStore.addObject(obj as any)
}

function cancel() {
  cleanupPreview()
  emit('close')
  resetState()
}

function resetState() {
  step.value = 'select'
  progress.value = 0
  loadingText.value = '正在解析文件...'
  modelName.value = ''
  selectedUnit.value = 'auto'
  faceCount.value = 0
  detectedMaxDim.value = 0
  detectedUnitScale.value = 1
  loadedGroup = null
  currentBuffer.value = null
}

// 初始文件（从外部拖入触发）
watch(() => props.initialFiles, (files) => {
  if (files && files.length > 0 && props.visible) {
    processFiles(files)
  }
}, { immediate: true })

onBeforeUnmount(() => {
  cleanupPreview()
})
</script>

<style scoped>
.import-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9000;
}

.import-dialog {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 12px;
  width: 560px;
  max-width: 95vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0,0,0,0.5);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid #334155;
}

.dialog-title {
  font-size: 15px;
  font-weight: 600;
  color: #e2e8f0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #64748b;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
}

.close-btn:hover { color: #e2e8f0; background: #334155; }

/* 步骤1：选择 */
.step-select {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.drop-zone {
  border: 2px dashed #334155;
  border-radius: 10px;
  padding: 40px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.drop-zone:hover, .drop-zone.dragging {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.drop-icon { color: #3b82f6; }

.drop-main { font-size: 15px; font-weight: 500; color: #e2e8f0; margin: 0; }
.drop-sub { font-size: 13px; color: #64748b; margin: 0; }
.drop-formats { font-size: 12px; color: #475569; margin: 0; }
.drop-formats-sub { font-size: 11px; color: #64748b; margin: 6px 0 0; line-height: 1.4; }

.tip-box {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 10px 14px;
}
.tip-text { margin: 0; font-size: 12px; color: #64748b; line-height: 1.5; }

/* 步骤2：加载 */
.step-loading {
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spin-icon {
  color: #3b82f6;
  animation: spin 1s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.loading-text { font-size: 14px; color: #94a3b8; margin: 0; }

.progress-bar {
  width: 100%;
  height: 6px;
  background: #334155;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 3px;
  transition: width 0.3s;
}

.progress-label { font-size: 12px; color: #64748b; }

/* 步骤3：预览 */
.step-preview {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.preview-layout {
  display: flex;
  flex: 1;
  gap: 0;
  overflow: hidden;
}

.preview-3d {
  width: 240px;
  min-height: 240px;
  background: #1a2332;
  flex-shrink: 0;
}

.preview-settings {
  flex: 1;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
}

.setting-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.setting-label {
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.setting-value {
  font-size: 13px;
  color: #e2e8f0;
}

.setting-input, .setting-select {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 6px;
  color: #e2e8f0;
  padding: 6px 10px;
  font-size: 13px;
  outline: none;
}

.setting-input:focus, .setting-select:focus {
  border-color: #3b82f6;
}

.unit-warning, .face-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: #f59e0b;
}

.face-notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: #60a5fa;
}

.warning-icon { flex-shrink: 0; margin-top: 1px; }

.face-danger { color: #f87171; }
.face-warn { color: #fbbf24; }

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid #334155;
}

.btn-cancel {
  background: #1e293b;
  border: 1px solid #334155;
  color: #94a3b8;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.btn-cancel:hover { border-color: #475569; color: #e2e8f0; }

.btn-confirm {
  background: #3b82f6;
  border: none;
  color: white;
  padding: 8px 18px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-confirm:hover { background: #2563eb; }
.btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
